import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectCurrentPlayer,
  selectGameType,
  selectHexes,
  selectPendingSupply,
  selectPhase,
  selectPhaseCompletedPlayers,
  selectPlayers,
  selectSelectedFarmsForConversion,
  selectSpecialBuildings,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {executePhaseAutoActions} from "../../gameState/turn/executePhaseAutoActions";
import {GAME_TYPES} from "../../../constants/game";
import {PHASES} from "../../../constants/phases";
import {COMMON_BUILDING_TOKENS} from "../../../constants/tokens";
import {POLICIES} from "../../../constants/policies";
import {isPlayerPolicy} from "../../factions/isPlayerPolicy";
import {agrarianismCommitConversion} from "../../../redux/actions";
import {calcFarmRemainingSupply} from "../../gameState/calcFarmRemainingSupply";
import {getBotGridRenderer} from "../botGrid";

/**
 * Волна 25 (23.2b, запечена): AGRARIANISM — остаток нераспределенного снабжения
 * ферм продается за монеты (3 снабжения -> 2 монеты, 2 -> 1), как это делает
 * живой игрок через PolicyAgrarianismToolbar. Без продажи остаток просто сгорает
 * в конце фазы. Вызывается ПОСЛЕ авто-распределения — отряды уже накормлены.
 * Возвращает true, если продажа состоялась.
 */
const sellAgrarianismLeftoverSupply = (store, actingPlayerId) => {
  const state = store.getState();
  // Одновременная фаза снабжения: продает остаток сам бот (actingPlayerId), а
  // не текущий игрок — тот в этой фазе только якорь слепка хода
  const currentPlayer = actingPlayerId
    ? selectPlayers(state).find((player) => player.id === actingPlayerId)
    : selectCurrentPlayer(state);

  if (!currentPlayer
    || !isPlayerPolicy(currentPlayer, POLICIES.AGRARIANISM)
  ) {
    return false;
  }

  const hexes = selectHexes(state);
  const units = selectUnits(state);
  const players = selectPlayers(state);
  const citiesCache = selectCitiesCache(state);
  const specialBuildings = selectSpecialBuildings(state);
  const pendingSupply = selectPendingSupply(state);
  const buildingsDamage = selectBuildingsDamage(state);
  const selectedFarmsForConversion = selectSelectedFarmsForConversion(state, currentPlayer.id);

  const playerFarms = (citiesCache?.[currentPlayer.color] || []).flatMap(
    (city) => city[COMMON_BUILDING_TOKENS.FARM] || []
  );

  // По одному элементу массива на каждую единицу нераспределенного снабжения
  // (формат PolicyAgrarianismToolbar: ферма повторяется столько раз, сколько
  // ее снабжения уходит в обмен)
  const leftoverFarms = [];
  playerFarms.forEach((farmHexKey) => {
    const remaining = calcFarmRemainingSupply(
      farmHexKey, selectedFarmsForConversion, hexes, specialBuildings,
      players, units, citiesCache, currentPlayer, pendingSupply, buildingsDamage
    );
    for (let i = 0; i < remaining; i++) {
      leftoverFarms.push(farmHexKey);
    }
  });

  // Меньше 2 снабжения монет не дает (2 -> 1 — минимальный обмен)
  if (leftoverFarms.length < 2) {
    return false;
  }

  store.dispatch(agrarianismCommitConversion(leftoverFarms, actingPlayerId));
  return true;
};

/**
 * Фаза SUPPLY: бот мгновенно принимает распределение общего авто-алгоритма
 * (executePhaseAutoActions) — того же, что работает для живых игроков; найм
 * d6 крысолюдов на свободных фермах алгоритм включает сам для ботов
 * (currentPlayer.isBot), независимо от того, локальная это игра или сетевая.
 * isClientExecutor передается по типу игры (гейт самого executePhaseAutoActions
 * требует false для сетевых игр — и для делегата-клиента, и для серверного
 * fallback-исполнения, иначе распределение не отрабатывает вовсе).
 * Если распределение детерминировано, алгоритм сам завершает фазу; иначе бот
 * принимает результат как есть и сразу завершает фазу (return false).
 */
export const decideSupplyStep = (store, actingPlayerId) => {
  const state = store.getState();
  const currentPlayer = selectCurrentPlayer(state);
  const isClientExecutor = selectGameType(state) === GAME_TYPES.LOCAL;

  // Одновременная фаза снабжения сетевой игры: бот раздает за себя
  // (actingPlayerId), и его часть фазы закончена, когда он попал в
  // phaseCompletedPlayers — текущий игрок в этой фазе не меняется
  if (actingPlayerId) {
    executePhaseAutoActions(store, isClientExecutor, getBotGridRenderer(state), null, {actingPlayerId});
    const afterState = store.getState();
    if (selectPhase(afterState) !== PHASES.SUPPLY
      || (selectPhaseCompletedPlayers(afterState) || []).includes(actingPlayerId)) {
      return true;
    }
    return sellAgrarianismLeftoverSupply(store, actingPlayerId);
  }

  executePhaseAutoActions(store, isClientExecutor, getBotGridRenderer(state));

  // Детерминированное распределение: фаза уже завершена авто-алгоритмом
  const afterState = store.getState();
  if (selectPhase(afterState) !== PHASES.SUPPLY) {
    return true;
  }
  const afterPlayer = selectCurrentPlayer(afterState);
  if (!afterPlayer || afterPlayer.id !== currentPlayer.id) {
    return true;
  }

  // Волна 25 (23.2b): фаза не завершена авто-алгоритмом (AGRARIANISM с 2+
  // фермами не автозавершается) — остаток снабжения продается за монеты
  return sellAgrarianismLeftoverSupply(store);
};
